import React, { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useProfile } from '@/hooks/useProfile';
import LandingShell from '@/components/landing/LandingShell';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import ModelSettings from '@/components/settings/ModelSettings';
import NewsManager from '@/components/settings/NewsManager';
import LobbySettings from '@/components/settings/LobbySettings';
import LogViewer from '@/components/settings/LogViewer';
import AnalyticsDashboard from '@/components/settings/AnalyticsDashboard';
import MarketingSettings from '@/components/settings/MarketingSettings';
import OrdersPanel from '@/components/admin/OrdersPanel';
import {
  Bot,
  Newspaper,
  Users,
  ScrollText,
  BarChart3,
  ShoppingCart,
  Megaphone,
  ShieldAlert,
} from 'lucide-react';

type AdminPanelId = 'models' | 'news' | 'lobby' | 'logs' | 'analytics' | 'orders' | 'marketing';

const NAV_ITEMS: Array<{
  id: AdminPanelId;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
}> = [
  { id: 'models', label: 'Models', description: 'Provider ordering, logos, and model defaults.', icon: Bot },
  { id: 'news', label: 'News', description: 'Publish and edit MapleOS news content.', icon: Newspaper },
  { id: 'lobby', label: 'Lobby', description: 'Manage private channels and memberships.', icon: Users },
  { id: 'logs', label: 'Logs', description: 'Inspect client event logs and user activity.', icon: ScrollText },
  { id: 'analytics', label: 'Analytics', description: 'Review growth, usage, and geography analytics.', icon: BarChart3 },
  { id: 'orders', label: 'Orders', description: 'Review product licenses and AI credit top-ups.', icon: ShoppingCart },
  { id: 'marketing', label: 'Marketing', description: 'Create and send marketing campaigns.', icon: Megaphone },
];

const isAdminPanel = (value: string | null): value is AdminPanelId =>
  value === 'models' ||
  value === 'news' ||
  value === 'lobby' ||
  value === 'logs' ||
  value === 'analytics' ||
  value === 'orders' ||
  value === 'marketing';

const renderPanel = (panel: AdminPanelId) => {
  switch (panel) {
    case 'models':
      return <ModelSettings />;
    case 'news':
      return <NewsManager />;
    case 'lobby':
      return <LobbySettings />;
    case 'logs':
      return <LogViewer />;
    case 'analytics':
      return <AnalyticsDashboard />;
    case 'orders':
      return <OrdersPanel />;
    case 'marketing':
      return <MarketingSettings />;
    default:
      return null;
  }
};

export default function Admin() {
  const { profile } = useProfile();
  const [searchParams, setSearchParams] = useSearchParams();

  const activePanel = useMemo<AdminPanelId>(() => {
    const panel = searchParams.get('panel');
    return isAdminPanel(panel) ? panel : 'models';
  }, [searchParams]);

  const activeItem = NAV_ITEMS.find((item) => item.id === activePanel) || NAV_ITEMS[0];

  const setActivePanel = (panel: AdminPanelId) => {
    const next = new URLSearchParams(searchParams);
    next.set('panel', panel);
    setSearchParams(next, { replace: true });
  };

  if (profile?.role !== 'admin') {
    return (
      <LandingShell showSectionLinks={false}>
        <div className="mx-auto w-full max-w-4xl px-6 py-10 md:py-14">
          <div className="rounded-2xl bg-card p-6 shadow-card md:p-10">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShieldAlert className="h-5 w-5 text-destructive" />
                  Admin access required
                </CardTitle>
                <CardDescription>
                  You do not have permission to view the MapleOS admin dashboard.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild variant="outline">
                  <Link to="/settings">Back to Settings</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </LandingShell>
    );
  }

  return (
    <LandingShell showSectionLinks={false}>
      <div className="mx-auto w-full max-w-7xl px-6 py-10 md:py-14">
        <div className="rounded-2xl bg-card p-6 shadow-card md:p-10">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[260px_minmax(0,1fr)]">
            <Card className="h-fit lg:sticky lg:top-24">
              <CardHeader>
                <CardTitle className="text-base">Admin Navigation</CardTitle>
                <CardDescription>Select a dashboard panel.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {NAV_ITEMS.map((item) => {
                  const Icon = item.icon;
                  const isActive = item.id === activePanel;

                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setActivePanel(item.id)}
                      className={`w-full rounded-lg border px-3 py-3 text-left transition ${
                        isActive ? 'border-primary bg-primary/10' : 'hover:bg-muted'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`mt-0.5 rounded-md p-2 ${isActive ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'}`}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium">{item.label}</div>
                          <div className="text-xs text-muted-foreground">{item.description}</div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </CardContent>
            </Card>

            <div className="min-w-0 space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>{activeItem.label}</CardTitle>
                  <CardDescription>{activeItem.description}</CardDescription>
                </CardHeader>
              </Card>
              {renderPanel(activePanel)}
            </div>
          </div>
        </div>
      </div>
    </LandingShell>
  );
}
